import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { REACTION_EMOJI } from "@shared/types";

/**
 * Emoji reaction bar for a task or comment. Shows aggregated counts (hover =
 * reactor names), highlights the emoji the current user picked, and offers a
 * small fixed-set picker. Toggling invalidates the reactions query so counts
 * update live without a reload.
 */
export function ReactionBar({
  targetType,
  targetId,
}: {
  targetType: "task" | "comment";
  targetId: string;
}) {
  const qc = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);

  const q = useQuery({
    queryKey: queryKeys.reactions(targetType, targetId),
    queryFn: () => api.reactions(targetType, targetId),
  });

  const toggle = useMutation({
    mutationFn: (emoji: string) =>
      api.toggleReaction(targetType, targetId, emoji),
    onSuccess: () => {
      setPickerOpen(false);
      qc.invalidateQueries({
        queryKey: queryKeys.reactions(targetType, targetId),
      });
    },
  });

  const reactions = q.data?.reactions ?? [];

  return (
    <div className="rxn-bar">
      {reactions.map((r) => (
        <button
          key={r.emoji}
          type="button"
          className={`rxn-chip${r.reactedByMe ? " on" : ""}`}
          title={r.users.join(", ")}
          onClick={() => toggle.mutate(r.emoji)}
          disabled={toggle.isPending}
        >
          <span className="rxn-emoji">{r.emoji}</span>
          <span className="rxn-count">{r.count}</span>
        </button>
      ))}
      <div className="rxn-add-wrap">
        <button
          type="button"
          className="rxn-add"
          aria-label="Add reaction"
          aria-expanded={pickerOpen}
          onClick={() => setPickerOpen((o) => !o)}
        >
          ☺+
        </button>
        {pickerOpen && (
          <div className="rxn-picker" role="menu">
            {REACTION_EMOJI.map((e) => (
              <button
                key={e}
                type="button"
                className="rxn-pick"
                onClick={() => toggle.mutate(e)}
                disabled={toggle.isPending}
              >
                {e}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
